import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  ChartBarIcon,
  ArrowUpTrayIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import useAuthStore from '../../store/authStore';
import useDocumentStore from '../../store/documentStore';
import { documentsAPI } from '../../services/api';
import { getDisplayTitle } from '../../utils/getDisplayTitle';

// Helper function to get meaningful display title


export default function Dashboard() {
  const { user } = useAuthStore();
  const { documents, isLoading: storeLoading } = useDocumentStore();
  const { fetchDocuments } = useDocumentStore.getState();

  // Use React Query for dashboard documents — cached across navigations
  const { data: dashDocs, isLoading: docsLoading } = useQuery({
    queryKey: ['dashboard-documents'],
    queryFn: async () => {
      await fetchDocuments({ limit: 10 });
      return true; // data lives in Zustand store
    },
    staleTime: 1000 * 60 * 5, // 5 min cache
  });

  // Use React Query for stats — cached across navigations
  const { data: stats = { total: 0, processed: 0, pending: 0, failed: 0 } } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await documentsAPI.stats();
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const isLoading = docsLoading && storeLoading;

  const statCards = [
    { title: 'Total Documents', value: stats.total, icon: DocumentTextIcon, color: 'orange' },
    { title: 'Processed', value: stats.processed, icon: CheckCircleIcon, color: 'green' },
    { title: 'Pending', value: stats.pending, icon: ClockIcon, color: 'amber' },
    { title: 'Failed', value: stats.failed, icon: ExclamationCircleIcon, color: 'red' },
  ];

  const getStatusBadge = (doc) => {
    if (doc.is_processed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-status-success/15 text-status-success text-xs font-medium">
          <CheckCircleIcon className="w-3.5 h-3.5" />
          Done
        </span>
      );
    }
    if (doc.has_error) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-status-error/15 text-status-error text-xs font-medium">
          <ExclamationCircleIcon className="w-3.5 h-3.5" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-theme-surface text-theme-secondary text-xs font-medium">
        <ClockIcon className="w-3.5 h-3.5" />
        Pending
      </span>
    );
  };

  const getStatColor = (color) => {
    switch (color) {
      case 'orange':
        return 'from-brand-orange to-brand-amber';
      case 'green':
        return 'from-status-success to-emerald-400';
      case 'amber':
        return 'from-brand-amber to-yellow-400';
      case 'red':
        return 'from-status-error to-red-400';
      default:
        return 'from-brand-orange to-brand-amber';
    }
  };

  return (
    <div className="space-y-5 pb-16 lg:pb-0">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-md shadow-brand-orange/25">
          <ChartBarIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-theme-primary">Dashboard</h1>
          <p className="text-sm text-theme-secondary">Welcome back! Here's an overview of your documents.</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-theme-tertiary rounded-lg p-4 border border-theme-primary shadow-theme-md transition-colors duration-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link
            to="/upload"
            className="group flex items-center gap-3 p-3 bg-theme-elevated rounded-lg border border-theme-primary hover:border-brand-orange/50 hover:bg-theme-surface transition-all duration-300"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-sm shadow-brand-orange/25 group-hover:scale-110 transition-transform">
              <ArrowUpTrayIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-theme-primary group-hover:text-brand-orange transition-colors">Upload Document</h3>
              <p className="text-xs text-theme-muted">Add new files</p>
            </div>
          </Link>
          <Link
            to="/documents"
            className="group flex items-center gap-3 p-3 bg-theme-elevated rounded-lg border border-theme-primary hover:border-status-success/50 hover:bg-theme-surface transition-all duration-300"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-status-success to-emerald-400 flex items-center justify-center shadow-sm shadow-status-success/25 group-hover:scale-110 transition-transform">
              <DocumentTextIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-theme-primary group-hover:text-status-success transition-colors">View Documents</h3>
              <p className="text-xs text-theme-muted">Browse library</p>
            </div>
          </Link>
          <Link
            to="/document-types"
            className="group flex items-center gap-3 p-3 bg-theme-elevated rounded-lg border border-theme-primary hover:border-purple-500/50 hover:bg-theme-surface transition-all duration-300"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-violet-400 flex items-center justify-center shadow-sm shadow-purple-500/25 group-hover:scale-110 transition-transform">
              <RectangleStackIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-theme-primary group-hover:text-purple-400 transition-colors">Document Types</h3>
              <p className="text-xs text-theme-muted">Configure schemas</p>
            </div>
          </Link>

        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.title}
              className="bg-theme-tertiary rounded-lg p-4 border border-theme-primary hover:border-theme-secondary transition-all shadow-theme-sm"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getStatColor(stat.color)} flex items-center justify-center shadow-sm`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-theme-primary">{stat.value}</p>
              <p className="text-sm text-theme-muted">{stat.title}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Documents */}
      <div className="bg-theme-tertiary rounded-lg border border-theme-primary overflow-hidden shadow-theme-md transition-colors duration-300">
        {/* Card Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-primary">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-sm shadow-brand-orange/25">
              <ClockIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-sm font-semibold text-theme-primary">Recent Documents</h2>
          </div>
          <Link
            to="/documents"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-brand-orange hover:text-brand-amber bg-brand-orange/10 hover:bg-brand-orange/20 rounded-md transition-colors"
          >
            View all
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Card Body */}
        <div className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner-theme w-6 h-6" />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <div className="w-12 h-12 rounded-lg bg-theme-elevated flex items-center justify-center mb-3">
                <DocumentTextIcon className="w-6 h-6 text-theme-muted" />
              </div>
              <h3 className="text-base font-semibold text-theme-primary mb-1">No documents yet</h3>
              <p className="text-sm text-theme-muted mb-4 max-w-sm">
                Upload your first document.
              </p>
              <Link
                to="/upload"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-amber text-white text-sm font-semibold rounded-lg shadow-sm shadow-brand-orange/25 hover:shadow-brand-orange/40 transition-all"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                Upload
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-theme-elevated">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider hidden md:table-cell">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider hidden sm:table-cell">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme-primary">
                  {documents.slice(0, 5).map((doc) => (
                    <tr
                      key={doc.id}
                      className="hover:bg-theme-elevated/50 transition-colors group"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/documents/${doc.slug || doc.id}`}
                          className="flex items-center gap-3"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-theme-elevated border border-theme-primary flex items-center justify-center group-hover:border-brand-orange/50 transition-all">
                            <DocumentTextIcon className="w-4 h-4 text-theme-muted group-hover:text-brand-orange transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-theme-primary group-hover:text-brand-orange transition-colors truncate max-w-[180px]">
                              {getDisplayTitle(doc)}
                            </p>
                            <p className="text-xs text-theme-muted">
                              {doc.pages_count ?? doc.pages?.length ?? 0} pages
                            </p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-theme-secondary bg-theme-elevated border border-theme-primary">
                          {doc.file_type?.toUpperCase() || 'PDF'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(doc)}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-theme-muted">
                          {new Date(doc.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
